import _ from "lodash";
import { create } from "zustand";
import { IChartGroup, SummaryChart } from "../../interfaces/ChartGroupInterface";
import { ChartType } from "./data";

/**
 * SummaryStore interface represents a store that manages summary charts.
 *
 * @interface
 */
interface SummaryStore {
    summaryCharts: SummaryChart[];
    chartGroups: IChartGroup[];
    toggleChartIsActive: (id: number) => void;
    setChartGroups: (groups: IChartGroup[]) => void;
    setCharts: (charts: SummaryChart[]) => void;
}

/**
 * Creates a summary store with methods to manage summary charts.
 * @typedef {Object} SummaryStore
 * @property {SummaryChart[]} summaryCharts - An array of summary charts.
 * @property {function(id: string): void} toggleChartIsActive - Toggles the isActive property of a summary chart with the specified id.
 * @property {function(charts: SummaryChart[]): void} setCharts - Sets the summaryCharts property to the specified array of summary charts.
 */
const useSummaryStore = create<SummaryStore>((set, get) => ({
    summaryCharts: [],
    chartGroups: [],

    toggleChartIsActive: (id: number) => {
        const updatedCharts = get().summaryCharts.map((chart) => {
            if (chart.id === id) {
                return { ...chart, isActive: !chart.isActive };
            }
            return chart;
        });

        set(() => ({ summaryCharts: updatedCharts }));
    },

    setChartGroups: (groups: IChartGroup[]) => {
        const validGroups = Array.isArray(groups) ? groups : [];

        const summaryCharts = _.uniqBy(
            validGroups.flatMap(group =>
                (group.charts ?? [])
                    .filter(Boolean)
                    .map(chart => ({
                        id: chart.id,
                        titleKey: chart.title,
                        descriptionKey: chart.description || null,
                        isActive: true,
                        fieldName: chart.fieldName,
                        yAxis: chart.yAxis || null,
                        layerId: chart.layerId,
                        lineField: chart.lineField || null,
                        yearField: chart.yearField || null,
                        lineFieldFilter: chart.lineFieldFilter?.length > 0 ? chart.lineFieldFilter : null,
                        chartType: ChartType[chart.chartType as keyof typeof ChartType],
                        groupID: group.label
                    }))
            ),
            'id'
        );

        set(() => ({ chartGroups: validGroups, summaryCharts }));
    },


    setCharts: (charts: SummaryChart[]) => {
        set(() => ({ summaryCharts: charts }));
    }
}));

export default useSummaryStore;